import PropTypes from "prop-types";
import { Box, Button, Chip, Slider, Stack, Typography } from "@mui/material";

import { getPipelineColor } from "./pipelineCategory";
import {
  ACCENT,
  ACCENT_TINT,
  FONT,
  HAIRLINE,
  INK,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
  SURFACE_MUTED,
  WEIGHT,
  withAlpha,
} from "../../../theme/tokens";

// Matches the sidebar width used by other browse-and-filter pages.
export const FILTER_PANEL_WIDTH = 300;

const PARAMETER_BUCKETS_B = [0, 4, 8, 16, 32, 64, 128, 256];
const PARAMETER_SCALE = 1_000_000_000;

export function formatParameterCount(value) {
  const count = Number(value || 0);
  if (!Number.isFinite(count) || count <= 0) return "0B";
  return `${(count / PARAMETER_SCALE).toFixed(count >= 10_000_000_000 ? 0 : 1)}B`;
}

function buildParameterMarks(maxParameterCount) {
  const max = Math.max(0, Number(maxParameterCount || 0));
  const options = PARAMETER_BUCKETS_B.map((value) => ({
    parameterValue: value * PARAMETER_SCALE,
    label: value === 0 ? "0" : `${value}B`,
  })).filter((option) => option.parameterValue <= max);

  const lastBucket = PARAMETER_BUCKETS_B[PARAMETER_BUCKETS_B.length - 1];
  const lastBucketValue = lastBucket * PARAMETER_SCALE;
  const hasMaxOption = options.some((option) => option.parameterValue === max);
  if (max > 0 && !hasMaxOption) {
    options.push({
      parameterValue: max,
      label:
        max > lastBucketValue
          ? `>${formatParameterCount(lastBucketValue)}`
          : formatParameterCount(max),
    });
  }

  return (options.length ? options : [{ parameterValue: 0, label: "0" }]).map(
    (option, index, allOptions) => ({
      ...option,
      value: index,
      label:
        index === 0 || index === allOptions.length - 1 || index % 2 === 0
          ? option.label
          : "",
    }),
  );
}

function findParameterMarkIndex(marks, parameterValue) {
  const index = marks.findIndex(
    (mark) => mark.parameterValue >= parameterValue,
  );
  return index === -1 ? marks.length - 1 : index;
}

function formatParameterMarkValue(marks, index) {
  const mark = marks[index];
  return mark ? formatParameterCount(mark.parameterValue) : "0B";
}

function FacetSection({ title, options, selectedValue, onSelect, getColor }) {
  if (!options.length) return null;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: FONT.sm,
          fontWeight: WEIGHT.semibold,
          color: INK_SECONDARY,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Stack direction="row" gap={0.75} flexWrap="wrap">
        {options.map((option) => {
          const selected = selectedValue === option.value;
          const categoryColor = getColor && getColor(option.value);
          // Pipeline facets keep the same color as model badges.
          if (categoryColor) {
            const fill = withAlpha(categoryColor, selected ? 0.22 : 0.12);
            return (
              <Chip
                key={option.value}
                label={`${option.label} ${option.count}`}
                clickable
                onClick={() => onSelect(selected ? "" : option.value)}
                sx={{
                  height: 30,
                  borderRadius: `${RADIUS.sm}px`,
                  border: `1px solid ${selected ? categoryColor : "transparent"}`,
                  bgcolor: fill,
                  color: categoryColor,
                  fontSize: FONT.sm,
                  fontWeight: selected ? WEIGHT.semibold : WEIGHT.medium,
                  "&:hover": {
                    bgcolor: withAlpha(categoryColor, 0.28),
                    borderColor: categoryColor,
                  },
                }}
              />
            );
          }

          return (
            <Chip
              key={option.value}
              label={`${option.label} ${option.count}`}
              clickable
              onClick={() => onSelect(selected ? "" : option.value)}
              variant={selected ? "filled" : "outlined"}
              sx={{
                height: 30,
                borderRadius: `${RADIUS.sm}px`,
                borderColor: selected ? ACCENT : HAIRLINE,
                bgcolor: selected ? ACCENT_TINT : SURFACE,
                color: selected ? ACCENT : INK_SECONDARY,
                fontSize: FONT.sm,
                fontWeight: selected ? WEIGHT.semibold : WEIGHT.medium,
                "&:hover": {
                  bgcolor: selected ? ACCENT_TINT : SURFACE_MUTED,
                  borderColor: ACCENT,
                },
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

FacetSection.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedValue: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  getColor: PropTypes.func,
};

export default function ModelFilterPanel({
  filters,
  maxParameterCount,
  onChange,
  organizations,
  pipelineTags,
}) {
  const parameterRange = filters.parameterRange || [0, maxParameterCount];
  const parameterMax = Math.max(0, maxParameterCount);
  const parameterMarks = buildParameterMarks(parameterMax);
  const [minParameter, maxParameter] = parameterRange;
  const parameterSliderRange = [
    findParameterMarkIndex(parameterMarks, minParameter),
    findParameterMarkIndex(parameterMarks, maxParameter),
  ];
  const hasActiveFilters =
    Boolean(filters.pipelineTag) ||
    Boolean(filters.organization) ||
    minParameter > 0 ||
    maxParameter < maxParameterCount;

  const updateFilter = (patch) => {
    onChange({ ...filters, ...patch });
  };

  const clearFilters = () => {
    onChange({
      pipelineTag: "",
      organization: "",
      parameterRange: null,
    });
  };

  const updateParameterRange = (range) => {
    updateFilter({
      parameterRange: range.map(
        (index) => parameterMarks[index]?.parameterValue ?? 0,
      ),
    });
  };

  const resetParameterRange = () => {
    updateFilter({ parameterRange: null });
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", lg: FILTER_PANEL_WIDTH },
        flexShrink: 0,
        border: `1px solid ${HAIRLINE}`,
        borderRadius: `${RADIUS.md}px`,
        bgcolor: SURFACE,
        p: 2,
        alignSelf: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          sx={{ fontSize: FONT.lg, fontWeight: WEIGHT.semibold, color: INK }}
        >
          Filters
        </Typography>
        {hasActiveFilters && (
          <Button
            size="small"
            onClick={clearFilters}
            sx={{
              textTransform: "none",
              fontSize: FONT.xs,
              color: ACCENT,
              minWidth: 0,
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <Stack gap={2.5}>
        <FacetSection
          title="Pipeline"
          options={pipelineTags}
          selectedValue={filters.pipelineTag}
          onSelect={(pipelineTag) => updateFilter({ pipelineTag })}
          getColor={getPipelineColor}
        />

        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: FONT.sm,
                fontWeight: WEIGHT.semibold,
                color: INK_SECONDARY,
              }}
            >
              Parameters
            </Typography>
            {(minParameter > 0 || maxParameter < maxParameterCount) && (
              <Button
                size="small"
                onClick={resetParameterRange}
                sx={{
                  minWidth: 0,
                  px: 0.75,
                  py: 0.25,
                  textTransform: "none",
                  fontSize: FONT.xs,
                  color: ACCENT,
                }}
              >
                Reset
              </Button>
            )}
          </Box>

          <Box sx={{ px: 2, pt: 1.25, pb: 2.75 }}>
            <Slider
              min={0}
              max={Math.max(0, parameterMarks.length - 1)}
              step={1}
              marks={parameterMarks}
              value={parameterSliderRange}
              disabled={maxParameterCount <= 0}
              onChange={(_event, value) => updateParameterRange(value)}
              disableSwap
              valueLabelDisplay="auto"
              valueLabelFormat={(value) =>
                formatParameterMarkValue(parameterMarks, value)
              }
              sx={{
                color: ACCENT,
                "& .MuiSlider-rail": { color: HAIRLINE },
                "& .MuiSlider-thumb": { width: 16, height: 16 },
                "& .MuiSlider-mark": {
                  height: 8,
                  bgcolor: HAIRLINE,
                },
                "& .MuiSlider-markActive": {
                  bgcolor: ACCENT,
                },
                "& .MuiSlider-markLabel": {
                  color: INK_SECONDARY,
                  fontSize: FONT.xs,
                  mt: 0.5,
                },
              }}
            />
          </Box>
        </Box>

        <FacetSection
          title="Organization"
          options={organizations}
          selectedValue={filters.organization}
          onSelect={(organization) => updateFilter({ organization })}
        />
      </Stack>
    </Box>
  );
}

ModelFilterPanel.propTypes = {
  filters: PropTypes.shape({
    organization: PropTypes.string.isRequired,
    parameterRange: PropTypes.arrayOf(PropTypes.number),
    pipelineTag: PropTypes.string.isRequired,
  }).isRequired,
  maxParameterCount: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  organizations: PropTypes.array.isRequired,
  pipelineTags: PropTypes.array.isRequired,
};
